import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Space,
  Popconfirm,
  Form,
  Input,
  Upload,
  Spin,
  Alert,
  Avatar,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useCategory } from "../contexts/CategoryContext";
import ScrollableTable from "../components/ScrollableTable";

const { TextArea } = Input;

const Categories: React.FC = () => {
  const {
    categories,
    meta,
    loading,
    error,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useCategory();

  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [form] = Form.useForm();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    fetchCategories({ page, limit });
    // eslint-disable-next-line
  }, [page, limit]);

  const handleAdd = () => {
    setEditingCategory(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingCategory(record);
    form.setFieldsValue({
      titleEn: record.title.en,
      titleAr: record.title.ar,
      descriptionEn: record.description.en,
      descriptionAr: record.description.ar,
      courseCount: record.courseCount,
    });
    setModalVisible(true);
  };

  const handleDelete = async (slug: string) => {
    await deleteCategory(slug);
    fetchCategories({ page, limit });
  };

  const handleFinish = async (values: any) => {
    const formData = new FormData();
    formData.append("title[en]", values.titleEn);
    formData.append("title[ar]", values.titleAr);
    formData.append("description[en]", values.descriptionEn);
    formData.append("description[ar]", values.descriptionAr);
    formData.append("courseCount", String(Number(values.courseCount)));
    if (values.image && values.image[0]?.originFileObj) {
      formData.append("image", values.image[0].originFileObj);
    }
    if (editingCategory) {
      await updateCategory(editingCategory.slug, formData);
    } else {
      await createCategory(formData);
    }
    setModalVisible(false);
    fetchCategories({ page, limit });
  };

  const columns = [
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      render: (img: string) =>
        img && <Avatar shape="square" size={48} src={img} />,
    },
    {
      title: "Slug",
      dataIndex: "slug",
      key: "slug",
      render: (slug: string) => <span>#{slug}</span>,
    },
    {
      title: "Title (EN)",
      dataIndex: ["title", "en"],
      key: "titleEn",
      render: (_: any, record: any) => record.title.en,
    },
    {
      title: "Title (AR)",
      dataIndex: ["title", "ar"],
      key: "titleAr",
      render: (_: any, record: any) => record.title.ar,
    },
    {
      title: "Course Count",
      dataIndex: "courseCount",
      key: "courseCount",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => date && new Date(date).toLocaleString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title="Are you sure to delete this category?"
            onConfirm={() => handleDelete(record.slug)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <h2>Categories</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Category
        </Button>
      </div>
      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 16 }} />
      )}
      <Spin spinning={loading} tip="Loading categories...">
        <ScrollableTable
          rowKey="slug"
          columns={columns}
          dataSource={categories}
          pagination={{
            current: meta?.page || 1,
            pageSize: meta?.limit || 10,
            total: meta?.total || 0,
            onChange: (p, ps) => {
              setPage(p);
              setLimit(ps);
            },
            showSizeChanger: true,
          }}
        />
      </Spin>
      <Modal
        open={modalVisible}
        title={editingCategory ? "Edit Category" : "Add Category"}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
            label="Title (EN)"
            name="titleEn"
            rules={[
              { required: true, message: "Please enter the English title" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Title (AR)"
            name="titleAr"
            rules={[
              { required: true, message: "Please enter the Arabic title" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Description (EN)"
            name="descriptionEn"
            rules={[
              {
                required: true,
                message: "Please enter the English description",
              },
            ]}
          >
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item
            label="Description (AR)"
            name="descriptionAr"
            rules={[
              {
                required: true,
                message: "Please enter the Arabic description",
              },
            ]}
          >
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item
            label="Course Count"
            name="courseCount"
            rules={[
              { required: true, message: "Please enter the course count" },
            ]}
          >
            <Input type="number" min={0} />
          </Form.Item>
          <Form.Item
            label="Image"
            name="image"
            valuePropName="fileList"
            getValueFromEvent={(e) => (Array.isArray(e) ? e : e && e.fileList)}
            rules={
              editingCategory
                ? []
                : [{ required: true, message: "Please upload an image" }]
            }
          >
            <Upload beforeUpload={() => false} maxCount={1} listType="picture">
              <Button icon={<UploadOutlined />}>Upload Image</Button>
            </Upload>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {editingCategory ? "Update" : "Create"} Category
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Categories;
